/**
 * RENDER — small Three.js conversion helpers for contract tuples.
 *
 * The contracts (`@/types`) use plain readonly tuples for Vec3/Quat/Transform so
 * every payload is JSON-serializable. These helpers convert them into Three.js
 * objects, writing into caller-provided targets where it matters for the hot path.
 */

import * as THREE from 'three';
import type { Vec3, Quat, Transform } from '@/types';

/** Write a contract Vec3 into a THREE.Vector3. Returns the target. */
export function setVec3(target: THREE.Vector3, v: Vec3): THREE.Vector3 {
  return target.set(v[0], v[1], v[2]);
}

/** Write a contract Quat into a THREE.Quaternion. Returns the target. */
export function setQuat(target: THREE.Quaternion, q: Quat): THREE.Quaternion {
  return target.set(q[0], q[1], q[2], q[3]);
}

/** Apply a contract Transform onto an Object3D (position + quaternion). */
export function applyTransform(obj: THREE.Object3D, t: Transform): void {
  obj.position.set(t.position[0], t.position[1], t.position[2]);
  obj.quaternion.set(t.rotation[0], t.rotation[1], t.rotation[2], t.rotation[3]);
}

/** Dispose a material (or array of materials) and any textures it references. */
export function disposeMaterial(material: THREE.Material | THREE.Material[]): void {
  const list = Array.isArray(material) ? material : [material];
  for (const m of list) {
    // Free any standard texture slots the material may hold.
    for (const key of MAT_TEXTURE_KEYS) {
      const value = (m as unknown as Record<string, unknown>)[key];
      if (value && value instanceof THREE.Texture) value.dispose();
    }
    m.dispose();
  }
}

const MAT_TEXTURE_KEYS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'emissiveMap',
  'aoMap',
  'alphaMap',
  'envMap',
  'bumpMap',
  'displacementMap',
] as const;

/**
 * Recursively dispose every geometry and material under an Object3D, then detach
 * it from its parent. Use for tearing down a whole subtree's GPU resources.
 */
export function disposeObject(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as Partial<THREE.Mesh> & THREE.Object3D;
    if (mesh.geometry && mesh.geometry instanceof THREE.BufferGeometry) {
      mesh.geometry.dispose();
    }
    if (mesh.material) disposeMaterial(mesh.material);
  });
  root.removeFromParent();
}
